import { useState, useEffect } from 'react';
import Gradient from 'gradient2';
import {
  PreviewContainer,
  PreviewKpi,
  PreviewInsight,
  PreviewValue,
  PreviewTable,
  ModelKpi,
  ModelBarChart,
  ModelBar,
  ModelAxis,
  ModelLegend,
  ModelLegendItem,
  ModelTable,
} from '../styled/DashStyles.styled';

interface TmpStyle {
  backgroundColor: string;
  borderLine: string;
  borderColor: string;
  borderRounded: string | number;
  borderSize: string | number;
  charts: string[];
  insightBg: string;
  insightTxt: string;
  valueBg: string;
  valueTxt: string;
  spaceBetween: string | number;
  useGlobalTheme: boolean;
  titles: string;
  tableHeader: string;
  tableRows: string[];
  shading: boolean;
  useGlobalFont: boolean;
  fontTitle: string;
  fontLabel: string;
  fontAxis: string;
  fontLegend: string;
  fontInsight: string;
  fontValue: string;
  fontTable: string;
}

const getContrastYIQ = (hexcolor: string, dark: string, light: string) => {
  // eslint-disable-next-line no-param-reassign
  hexcolor = hexcolor.replace('#', '');
  const r = parseInt(hexcolor.substr(0, 2), 16);
  const g = parseInt(hexcolor.substr(2, 2), 16);
  const b = parseInt(hexcolor.substr(4, 2), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? dark : light;
};

const getFontColor = (hexcolor: string) => getContrastYIQ(hexcolor, '#000', '#FFF');

const genCardPreviewStyle = (tmpStyle: TmpStyle) => ({
  borderStyle: tmpStyle.borderLine,
  borderColor: tmpStyle.borderColor,
  borderRadius: tmpStyle.borderRounded,
  borderWidth: tmpStyle.borderSize,
  boxShadow: tmpStyle.shading ? '0px 4px 4px #686868A6' : '',
});

const barsValues = [100, 80, 60, 75, 85];
const barsCat = ['Cat A', 'Cat B', 'Cat C', 'Cat D', 'Cat D'];

const randomValsList = Array.from({ length: 12 }, () => {
  const num = parseFloat((Math.random() * 999.99).toFixed(2));
  return num.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
});

const Preview = ({ tmpStyle }: { tmpStyle: TmpStyle }) => {
  const [gradientPreview, setGradientPreview] = useState<string[]>([]);

  useEffect(() => {
    const gradient = new Gradient({
      colors: tmpStyle.useGlobalTheme ? (
        tmpStyle.charts.filter((c) => Boolean(c))
      ) : ['#4711B2', '#4711B2'],
      steps: 5,
    });
    setGradientPreview(gradient.toArray());
  }, [tmpStyle.charts, tmpStyle.useGlobalTheme]);

  return (
    <PreviewContainer style={{ gridGap: `${+(tmpStyle.spaceBetween) * 2}px` }}>
      <PreviewKpi
        style={{ ...genCardPreviewStyle(tmpStyle), backgroundColor: tmpStyle.backgroundColor }}
      >
        <i style={{
          ...(tmpStyle.useGlobalTheme ? { color: tmpStyle.titles } : {}),
          ...(tmpStyle.useGlobalFont ? { fontFamily: tmpStyle.fontTitle } : {}),
        }}
        >
          Gráficos
        </i>
        <ModelKpi>
          <ModelAxis style={{
            ...(tmpStyle.useGlobalFont ? { fontFamily: tmpStyle.fontAxis } : {}),
            color: getFontColor(tmpStyle.backgroundColor),
          }}
          >
            <span>100</span>
            <span>80</span>
            <span>60</span>
            <span>40</span>
            <span>20</span>
            <span>0</span>
          </ModelAxis>
          <ModelBarChart>
            {gradientPreview.map((c, i) => (
              <ModelBar
                key={c + i.toString()}
                style={{
                  backgroundColor: c,
                  height: `${barsValues[i]}%`,
                  ...(tmpStyle.useGlobalFont ? { fontFamily: tmpStyle.fontLabel } : {}),
                  color: getFontColor(tmpStyle.backgroundColor),
                }}
              >
                <span>{barsValues[i]}</span>
              </ModelBar>
            ))}
          </ModelBarChart>
          <ModelLegend>
            {gradientPreview.map((c, i) => (
              <ModelLegendItem
                key={`${c + i.toString()}-legend`}
                style={{
                  ...(tmpStyle.useGlobalFont ? { fontFamily: tmpStyle.fontLegend } : {}),
                  color: getFontColor(tmpStyle.backgroundColor),
                }}
              >
                <div style={{ backgroundColor: c }} />
                <span>{barsCat[i]}</span>
              </ModelLegendItem>
            ))}
          </ModelLegend>
        </ModelKpi>
      </PreviewKpi>
      <PreviewInsight
        style={{
          ...genCardPreviewStyle(tmpStyle),
          ...(tmpStyle.useGlobalTheme ? { backgroundColor: tmpStyle.insightBg } : {}),
        }}
      >
        <b
          style={{
            ...(tmpStyle.useGlobalTheme ? { color: tmpStyle.insightTxt } : {}),
            ...(tmpStyle.useGlobalFont ? { fontFamily: tmpStyle.fontInsight } : {}),
          }}
        >
          Insight
        </b>
      </PreviewInsight>
      <PreviewValue
        style={{
          ...genCardPreviewStyle(tmpStyle),
          ...(tmpStyle.useGlobalTheme ? { backgroundColor: tmpStyle.valueBg } : {}),
        }}
      >
        <i style={{
          ...(tmpStyle.useGlobalTheme ? { color: tmpStyle.titles } : {}),
          ...(tmpStyle.useGlobalFont ? { fontFamily: tmpStyle.fontTitle } : {}),
        }}
        >
          Valor
        </i>
        <b
          style={{
            ...(tmpStyle.useGlobalTheme ? { color: tmpStyle.valueTxt } : {}),
            ...(tmpStyle.useGlobalFont ? { fontFamily: tmpStyle.fontValue } : {}),
          }}
        >
          987,01
        </b>
      </PreviewValue>
      <PreviewTable
        style={{ ...genCardPreviewStyle(tmpStyle), backgroundColor: tmpStyle.backgroundColor }}
      >
        <i style={{
          ...(tmpStyle.useGlobalTheme ? { color: tmpStyle.titles } : {}),
          ...(tmpStyle.useGlobalFont ? { fontFamily: tmpStyle.fontTitle } : {}),
        }}
        >
          Tabela
        </i>
        <ModelTable style={{
          ...(tmpStyle.useGlobalFont ? { fontFamily: tmpStyle.fontTable } : {}),
        }}
        >
          <tr>
            <th style={tmpStyle.useGlobalTheme ? {
              backgroundColor: tmpStyle.tableHeader,
              color: getFontColor(tmpStyle.tableHeader),
            } : {}}
            >
              {barsCat[0]}
            </th>
            <th style={tmpStyle.useGlobalTheme ? {
              backgroundColor: tmpStyle.tableHeader,
              color: getFontColor(tmpStyle.tableHeader),
            } : {}}
            >
              {barsCat[1]}
            </th>
            <th style={tmpStyle.useGlobalTheme ? {
              backgroundColor: tmpStyle.tableHeader,
              color: getFontColor(tmpStyle.tableHeader),
            } : {}}
            >
              {barsCat[2]}
            </th>
          </tr>
          {[...Array(4).keys()].map((key, i) => (
            <tr key={`table-${key}`}>
              <td
                style={
                  tmpStyle.useGlobalTheme ? {
                    backgroundColor: tmpStyle.tableRows[(i + 1) % 2],
                    color: getFontColor(tmpStyle.tableRows[(i + 1) % 2]),
                  } : {}
                }
              >
                {randomValsList[3 * i]}
              </td>
              <td
                style={
                  tmpStyle.useGlobalTheme ? {
                    backgroundColor: tmpStyle.tableRows[(i + 1) % 2],
                    color: getFontColor(tmpStyle.tableRows[(i + 1) % 2]),
                  } : {}
                }
              >
                {randomValsList[(3 * i) + 1]}
              </td>
              <td
                style={
                  tmpStyle.useGlobalTheme ? {
                    backgroundColor: tmpStyle.tableRows[(i + 1) % 2],
                    color: getFontColor(tmpStyle.tableRows[(i + 1) % 2]),
                  } : {}
                }
              >
                {randomValsList[(3 * i) + 2]}
              </td>
            </tr>
          ))}
        </ModelTable>
      </PreviewTable>
    </PreviewContainer>
  );
};

export default Preview;
